/**
 * Unified list helper
 * Single source of truth for filtering and counting friends/groups list items
 */

import type { FriendListItem } from '../hooks/useFriendData'
import type { GroupListItem } from '../hooks/useGroupsData'
import {
  VIEW_TYPE,
  FILTER_TYPE,
  type ViewType,
  type FilterType,
} from '../constants/viewFilter.constants'
import { GROUP_LIST_ITEM_TYPE } from '../constants/groups.constants'

export type UnifiedListItem = FriendListItem | GroupListItem

/** Sentinel item for the "Invite" card shown last in the friends grid (Friends view only). */
export const INVITE_BUTTON_ITEM = { type: 'INVITE', id: 'invite-button' } as const
export type InvitePlaceholderItem = typeof INVITE_BUTTON_ITEM

/** Sentinel item for the "Create group" card shown last in the groups grid (Groups view only). */
export const CREATE_GROUP_BUTTON_ITEM = { type: 'CREATE_GROUP', id: 'create-group-button' } as const
export type CreateGroupPlaceholderItem = typeof CREATE_GROUP_BUTTON_ITEM

/** Item type for FriendsGrid when showing the invite or create-group card as last cell. */
export type FriendsGridItem = UnifiedListItem | InvitePlaceholderItem | CreateGroupPlaceholderItem

/** Item type strings per view; used for config-driven filtering */
type FriendItemType = FriendListItem['type']
type GroupItemType = GroupListItem['type']

export interface UnifiedListCounts {
  friend: number
  pending: number
}

export interface FilterUnifiedListResult {
  filteredItems: UnifiedListItem[]
  counts: UnifiedListCounts
}

/**
 * Config: which item types count as "active" vs "pending" per view.
 * Exhaustive: every FriendListItem type and every GroupListItem type is in exactly one array.
 */
const VIEW_STATUS_CONFIG: Record<
  ViewType,
  { activeStatuses: readonly FriendItemType[] | readonly GroupItemType[]; pendingStatuses: readonly FriendItemType[] | readonly GroupItemType[] }
> = {
  [VIEW_TYPE.FRIENDS]: {
    activeStatuses: ['REQUEST', 'FRIEND'] as const,
    pendingStatuses: [FILTER_TYPE.PENDING] as const,
  },
  [VIEW_TYPE.GROUPS]: {
    activeStatuses: [GROUP_LIST_ITEM_TYPE.GROUP_ACTIVE] as const,
    pendingStatuses: [GROUP_LIST_ITEM_TYPE.GROUP_PENDING] as const,
  },
}

/**
 * Empty state copy for Friends vs Groups view
 */
export const EMPTY_STATE_COPY: Record<ViewType, { title: string; subtitle: string }> = {
  [VIEW_TYPE.FRIENDS]: {
    title: 'friends',
    subtitle: 'Start building your network by inviting friends to join!',
  },
  [VIEW_TYPE.GROUPS]: {
    title: 'groups',
    subtitle: 'Create or join groups to get started!',
  },
}

function getItemType(item: UnifiedListItem): string {
  return item.type
}

function isFriendItem(item: UnifiedListItem): item is FriendListItem {
  return 'friend' in item
}

function isGroupItem(item: UnifiedListItem): item is GroupListItem {
  return 'group' in item
}

/**
 * Get active and pending counts for unified list items (friends or groups)
 */
export function getUnifiedListCounts(
  items: UnifiedListItem[],
  viewType: ViewType
): UnifiedListCounts {
  const config = VIEW_STATUS_CONFIG[viewType]
  const activeSet = new Set(config.activeStatuses)
  const pendingSet = new Set(config.pendingStatuses)
  let activeCount = 0
  let pendingCount = 0

  const isRelevant = viewType === VIEW_TYPE.FRIENDS ? isFriendItem : isGroupItem

  for (const item of items) {
    if (!isRelevant(item)) continue
    const type = getItemType(item)
    if (activeSet.has(type as FriendItemType & GroupItemType)) activeCount++
    else if (pendingSet.has(type as FriendItemType & GroupItemType)) pendingCount++
    // Exhaustive: every REQUEST|FRIEND|PENDING and GROUP_ACTIVE|GROUP_PENDING is in one set
  }

  return { friend: activeCount, pending: pendingCount }
}

/**
 * Filter unified list items by tab (Active/Pending) and compute counts.
 * Config-driven: ViewType → statuses; each item lands in at most one bucket.
 */
export function filterUnifiedListItems(
  items: UnifiedListItem[],
  filterType: FilterType,
  viewType: ViewType
): FilterUnifiedListResult {
  const counts = getUnifiedListCounts(items, viewType)
  const config = VIEW_STATUS_CONFIG[viewType]
  const activeSet = new Set(config.activeStatuses)
  const pendingSet = new Set(config.pendingStatuses)
  const isRelevant = viewType === VIEW_TYPE.FRIENDS ? isFriendItem : isGroupItem

  const activeItems: UnifiedListItem[] = []
  const pendingItems: UnifiedListItem[] = []

  for (const item of items) {
    if (!isRelevant(item)) continue
    const type = getItemType(item)
    if (activeSet.has(type as FriendItemType & GroupItemType)) activeItems.push(item)
    else if (pendingSet.has(type as FriendItemType & GroupItemType)) pendingItems.push(item)
    // Exhaustive: no item lands in both; unknown types are skipped
  }

  // Groups view: show all (no Active/Pending tab); Friends view: filter by tab
  const filteredItems =
    viewType === VIEW_TYPE.GROUPS
      ? [...pendingItems, ...activeItems]
      : filterType === FILTER_TYPE.FRIEND
        ? activeItems
        : pendingItems
  return { filteredItems, counts }
}
